/**
 * Service page.
 *
 * One conversion frame, repeated for every service, exactly as the brief
 * specifies: hero + CTAs, trust, what it solves, what's included, process,
 * equipment, before/after, how we quote, related services, service FAQs, and a
 * closing CTA. Each of these pages is a Google Ads landing page, which is why
 * the quote action is repeated rather than left to the bottom.
 *
 * Everything is CMS content. A section with nothing behind it is omitted
 * rather than rendered empty — a heading with no body reads as broken.
 */
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import PageHero from "../components/PageHero";
import Eyebrow from "../components/shared/Eyebrow";
import WhatsAppButton from "../components/shared/WhatsAppButton";
import BeforeAfterGallery from "../components/sections/BeforeAfterGallery";
import FinalCTA from "../components/sections/FinalCTA";
import NotFound from "./NotFound";
import { serviceService } from "../services/serviceService";
import { useRemoteData } from "../hooks/useRemoteData";
// fadeUp defines `hidden` and `show` — not `visible`. Naming the wrong
// variant leaves the element stuck at opacity 0, which renders the section
// heading with nothing beneath it.
import { fadeUp, viewport } from "../lib/motion";

export default function ServiceDetail() {
  const { slug } = useParams();

  const { data: service, loading } = useRemoteData(
    () => serviceService.detail(slug),
    [slug],
    { initialValue: null }
  );

  /**
   * This route is `/:slug`, so it receives every unmatched single-segment URL,
   * not just service pages. Until the lookup resolves we cannot know which it
   * is — so render nothing rather than flashing a 404 at someone who typed a
   * perfectly good service URL.
   */
  if (loading) {
    return <div className="min-h-[60vh] bg-ink" aria-busy="true" />;
  }

  if (!service) return <NotFound />;

  return (
    <>
      {/* defaultService pre-selects this service in the hero quote form. */}
      <PageHero
        eyebrow={service.group || "Our Services"}
        headline={service.heroHeadline || service.name}
        intro={service.heroIntro || service.shortDescription}
        image={service.heroImage}
        imageAlt={service.heroImageAlt || service.name}
        defaultService={service.name}
      />

      {/*
        A second quote action, for anyone who scrolled past the hero form.
      */}
      <section className="bg-ink border-y border-charcoal/50 py-6">
        <div className="container-emilio flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <p className="text-sm text-mutedtext">
            Send photos for a more accurate quotation — most enquiries are
            answered the same day.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-emerald text-ink px-6 py-3 text-sm font-semibold hover:bg-emerald-light transition-colors"
              data-testid="service-quote-top"
            >
              {service.ctaLabel || "Get a Quote"} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <WhatsAppButton
              label="WhatsApp Us"
              service={service.name}
              message={service.whatsappMessage}
            />
          </div>
        </div>
      </section>

      {service.problemHtml && (
        <Section eyebrow="What This Solves">
          <div
            className="prose-emilio max-w-3xl text-white/90 text-base md:text-lg leading-[1.8] space-y-5"
            dangerouslySetInnerHTML={{ __html: service.problemHtml }}
          />
        </Section>
      )}

      {service.checklist?.length > 0 && (
        <Section eyebrow="What's Included" title="Scope of work">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {service.checklist.map((group, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
                className="border border-charcoal p-6"
              >
                <h3 className="font-display font-bold text-white text-lg">{group.title}</h3>
                <ul className="mt-4 space-y-2.5">
                  {(group.items || []).map((item, itemIndex) => (
                    <li key={itemIndex} className="flex gap-3 text-sm text-white/85">
                      <Check className="w-4 h-4 text-emerald mt-0.5 shrink-0" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
          {service.checklistNote && (
            <p className="mt-6 text-sm text-mutedtext max-w-2xl">{service.checklistNote}</p>
          )}
        </Section>
      )}

      {service.process?.length > 0 && (
        <Section eyebrow="Our Process" title="How the work is carried out">
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.process.map((step, index) => (
              <li key={index} className="border border-charcoal p-6">
                <span className="font-mono text-emerald text-sm">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-3 font-display font-bold text-white">{step.title}</h3>
                {step.body && (
                  <p className="mt-2 text-sm text-mutedtext leading-relaxed">{step.body}</p>
                )}
              </li>
            ))}
          </ol>
        </Section>
      )}

      {service.equipmentHtml && (
        <Section eyebrow="Equipment & Method">
          <div
            className="prose-emilio max-w-3xl text-white/90 leading-[1.8] space-y-5"
            dangerouslySetInnerHTML={{ __html: service.equipmentHtml }}
          />
        </Section>
      )}

      <BeforeAfterGallery />

      {(service.pricingFactors?.length > 0 || service.pricingNote) && (
        <Section eyebrow="How We Quote" title="What affects the price">
          {service.startingPrice && (
            <p className="text-2xl font-display font-bold text-emerald mb-6">
              From {service.startingPrice}
            </p>
          )}
          {service.pricingFactors?.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
              {service.pricingFactors.map((factor, index) => (
                <li key={index} className="flex gap-3 text-sm text-white/85">
                  <Check className="w-4 h-4 text-emerald mt-0.5 shrink-0" aria-hidden="true" />
                  <span>{factor}</span>
                </li>
              ))}
            </ul>
          )}
          {service.pricingNote && (
            <p className="mt-6 text-sm text-mutedtext max-w-2xl leading-relaxed">
              {service.pricingNote}
            </p>
          )}
        </Section>
      )}

      {service.related?.length > 0 && (
        <Section eyebrow="Combine With" title="Services that go together">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {service.related.map((related) => (
              <Link
                key={related.slug}
                to={related.route}
                className="group border border-charcoal hover:border-emerald/60 transition-colors"
                data-testid={`related-${related.slug}`}
              >
                {related.heroImage && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={related.heroImage}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-display font-bold text-white group-hover:text-emerald transition-colors">
                    {related.name}
                  </h3>
                  <p className="mt-2 text-sm text-mutedtext">{related.shortDescription}</p>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {service.faqs?.length > 0 && (
        <Section eyebrow="Questions" title={`${service.name} FAQs`}>
          <div className="max-w-3xl divide-y divide-charcoal border-y border-charcoal">
            {service.faqs.map((faq, index) => (
              <details key={index} className="group py-5">
                <summary className="cursor-pointer list-none font-display font-semibold text-white flex justify-between gap-4">
                  {faq.question}
                  <span className="text-emerald group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-mutedtext leading-relaxed whitespace-pre-line">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </Section>
      )}

      <FinalCTA />
    </>
  );
}

/** Consistent section shell, so every block on the page lines up. */
function Section({ eyebrow, title, children }) {
  return (
    <section className="bg-ink py-20 md:py-24 border-t border-charcoal/50">
      <div className="container-emilio">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {title && (
          <h2 className="mt-4 mb-10 font-display font-extrabold text-white text-display">
            {title}
          </h2>
        )}
        {!title && <div className="mt-6" />}
        {children}
      </div>
    </section>
  );
}
